import { useEffect, useMemo } from 'react';
import { isSameDay } from 'shared/util/datetime-fns';

// Generates HH:mm formatted time strings at custom minute intervals
const createRawTimeValues = (stepMinutes: number): string[] => {
    const times: string[] = [];
    const totalSteps = (24 * 60) / stepMinutes;

    for (let i = 0; i < totalSteps; i++) {
        const totalMinutes = i * stepMinutes;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        times.push(time);
    }
    return times;
};

export const useTimePicker = ({
    pickupDate,
    returnDate,
    pickupTime,
    returnTime,
    onReturnTimeChange,
    interval = 30,
}: {
    pickupDate: Date;
    returnDate: Date;
    pickupTime: string;
    returnTime: string;
    onReturnTimeChange: (value: string) => void;
    interval?: number;
}) => {
    const allTimes = useMemo(() => createRawTimeValues(interval), [interval]);
    const sameDay = !!pickupDate && !!returnDate && isSameDay(pickupDate, returnDate);

    useEffect(() => {
        if (!sameDay || !pickupTime || !returnTime) return;

        if (returnTime <= pickupTime) {
            const next = allTimes.find((time) => time > pickupTime);
            if (next && next !== returnTime) {
                setTimeout(() => {
                    onReturnTimeChange(next);
                }, 0);
            }
        }
    }, [pickupTime, returnTime, sameDay, interval, allTimes, onReturnTimeChange]);

    const returnTimeOptions = useMemo(
        () => (sameDay ? allTimes.filter((time) => time > pickupTime) : allTimes),
        [sameDay, allTimes, pickupTime],
    );

    return {
        allTimes,
        returnTimeOptions,
    };
};
